import * as yup from "yup";
import { useInfiniteQuery } from "@tanstack/react-query";
import { HTTP_ENDPOINTS, QUERY_KEYS } from "../../consts/api";
import { http } from "../../services/HttpService";
import { removeNullishFromShape, unwrapOr } from "../../utilities/general";
import {
  composePaginationSchema,
  getNextPaginationPage,
  paginationSchema,
} from "../../schemas/pagination";

const requestSchema = yup.object({
  pageSize: yup.number().required(),
  days: yup.number().notRequired(),
  from: yup.string().notRequired(),
  to: yup.string().notRequired(),
  clinicId: yup.number().nullable(),
});

const stringOrNumber = yup
  .mixed()
  .test(
    "string-or-number",
    "must be string or number",
    (val) => val == null || typeof val === "string" || typeof val === "number",
  );

const responseSchema = paginationSchema.concat(
  yup.object({
    reports: yup
      .array()
      .of(
        yup.object({
          date: yup.string().required(),
          patientName: yup.string().required(),
          clinicId: yup.number().nullable(),
          clinicName: yup.string().nullable(),
          productName: yup.string().nullable(),
          unitsCount: yup.number().required(),
          dollarValue: stringOrNumber.required(),
          balanceUnitsCount: yup.number().required(),
          walletBalanceDollarValue: stringOrNumber.required(),
          balanceServiceValue: stringOrNumber.required(),
        }),
      )
      .required(),

    stats: yup
      .object({
        total: yup.string().nullable(),
      })
      .required(),
  }),
);

const composeResponse = (res, req) => {
  const reports = unwrapOr(() => res.data.data.data, []);
  return {
    ...composePaginationSchema({
      response: unwrapOr(() => res.data.data, {}),
      pageSize: req.pageSize,
    }),
    reports: reports.map((report) => ({
      // base fields
      date: report.date,
      patientId: report.patient_id,
      patientName: report.client,
      patientEmail: report.email,
      clinicId: report.clinic_id,
      clinicName: report.clinic_name,
      productName: report.product_name,
      unitsCount: report.units || 0,
      dollarValue: report.dollar_value,
      balanceUnitsCount: report.balance_units || 0,
      walletBalanceDollarValue: report.wallet_balance_dollar_value,
      balanceServiceValue: report.balance_service_value,
      itemSource: report.item_source,
      // optional ids
      invoiceId: report.invoice_id,
      packageId: report.package_id,
      // children items as-is
      items: Array.isArray(report.items) ? report.items : [],
    })),
    stats: {
      total: res?.data?.data?.total_liability || null,
    },
  };
};

function createSalesExpiringLiabilityReportQueryKey({
  pageSize,
  days,
  from,
  to,
  clinicId,
}) {
  return [
    QUERY_KEYS.expiredLiabilityReport,
    pageSize,
    days,
    from,
    to,
    clinicId,
  ];
}

export function useSalesExpiringLiabilityReportQuery({
  payload = {},
  options = {},
} = {}) {
  return useInfiniteQuery({
    queryKey: createSalesExpiringLiabilityReportQueryKey({
      pageSize: payload.pageSize,
      days: payload.days,
      from: payload.from,
      to: payload.to,
      clinicId: payload.clinicId,
    }),
    queryFn: async ({ pageParam = 1 }) => {
      const req = requestSchema.validateSync(payload, {
        strict: true,
      });
      const res = await http.post(
        HTTP_ENDPOINTS.createExpiringLiabilityReport(),
        removeNullishFromShape({
          page: pageParam,
          pagesize: req.pageSize,
          days: req.days,
          from: req.from,
          to: req.to,
          clinic_id: req.clinicId,
        }),
      );
      const composed = composeResponse(res, req);
      try {
        return responseSchema.validateSync(composed, { strict: true });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Expiring liability response validation warning:", e);
        return composed;
      }
    },
    ...options,
    getNextPageParam: getNextPaginationPage,
  });
}
